import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from './Toast';

const ToastContext = createContext(null);

const MAX_VISIBLE = 4;
const DEFAULT_DURATION = 5000;
const ERROR_DURATION = 7000;

let idSeq = 0;
const nextId = () => `t_${Date.now()}_${++idSeq}`;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const h = timers.current.get(id);
    if (h) { clearTimeout(h); timers.current.delete(id); }
  }, []);

  const push = useCallback((variant, { title, subtitle, duration } = {}) => {
    if (!title) return null;
    const id = nextId();
    const ttl = duration ?? (variant === 'error' ? ERROR_DURATION : DEFAULT_DURATION);
    setToasts((list) => {
      const next = [...list, { id, variant, title, subtitle }];
      return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
    });
    if (ttl > 0) {
      const h = setTimeout(() => dismiss(id), ttl);
      timers.current.set(id, h);
    }
    return id;
  }, [dismiss]);

  useEffect(() => () => {
    for (const h of timers.current.values()) clearTimeout(h);
    timers.current.clear();
  }, []);

  const api = useMemo(() => ({
    success: (opts) => push('success', opts),
    info:    (opts) => push('info', opts),
    warning: (opts) => push('warning', opts),
    error:   (opts) => push('error', opts),
    neutral: (opts) => push('neutral', opts),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-label="Avisos do sistema"
        className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md sm:w-full z-[100] pointer-events-none flex flex-col items-stretch sm:items-end gap-3"
      >
        {toasts.map((t) => (
          <Toast key={t.id} id={t.id} variant={t.variant} title={t.title} subtitle={t.subtitle} onClose={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de <ToastProvider>');
  return ctx;
}
